import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const TrialStatusContext = createContext();

export const TrialStatusProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [status, setStatus] = useState({
    accountStatus: null, // "ACTIVE" | "TRIAL" | "EXPIRED" | null
    trialDaysRemaining: null,
    trialEndsAt: null,
    isActive: null,
    loading: false,
    error: null,
  });

  const [isExpiredModalOpen, setIsExpiredModalOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setStatus((prev) => ({ ...prev, loading: false }));
      return null;
    }
    setStatus((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/company/basic`);
      const data = res?.data || {};
      const accountStatus = data.account_status || null;
      const trialDaysRemaining = typeof data.trial_days_remaining === "number" ? data.trial_days_remaining : null;
      const trialEndsAt = data.trial_ends_at || null;
      const isActive = typeof data.is_active === "boolean" ? data.is_active : null;
      setStatus({ accountStatus, trialDaysRemaining, trialEndsAt, isActive, loading: false, error: null });
      // Auto-open expired modal if expired
      if (accountStatus === "EXPIRED" || (isActive === false && (trialDaysRemaining ?? 0) <= 0)) {
        setIsExpiredModalOpen(true);
      }
      return data;
    } catch (err) {
      setStatus((prev) => ({ ...prev, loading: false, error: err }));
      return null;
    }
  }, [isAuthenticated]);

  // Initial load and when auth state changes
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Install a global interceptor to catch capability gating (HTTP 402)
  const interceptorIdRef = useRef(null);
  useEffect(() => {
    // Eject previous
    if (interceptorIdRef.current !== null) {
      axios.interceptors.response.eject(interceptorIdRef.current);
      interceptorIdRef.current = null;
    }
    interceptorIdRef.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const statusCode = error?.response?.status;
        const detail = error?.response?.data?.detail || error?.response?.data?.message;
        if (statusCode === 402 && String(detail || "").toUpperCase().includes("TRIAL_EXPIRED")) {
          // Refresh account status and show modal
          await fetchStatus();
          setIsExpiredModalOpen(true);
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorIdRef.current !== null) {
        axios.interceptors.response.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [fetchStatus]);

  const value = useMemo(
    () => ({
      ...status,
      refreshStatus: fetchStatus,
      isExpiredModalOpen,
      openExpiredModal: () => setIsExpiredModalOpen(true),
      closeExpiredModal: () => setIsExpiredModalOpen(false),
    }),
    [status, fetchStatus, isExpiredModalOpen]
  );

  return <TrialStatusContext.Provider value={value}>{children}</TrialStatusContext.Provider>;
};

export const useTrialStatus = () => useContext(TrialStatusContext);


